import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Paper, Button, Link } from '@mui/material';
import { UploadFile, OpenInNew } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { AdmissionCycle, AdmissionApplication, ApplicationDocument, DocumentChecklistItem } from '../../../types/admissions';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { toUploadUrl } from '../../../utils/uploadUrl';
import { ToneChip, ChipTone } from '../../common/StatusChip';
import DocumentRejectDialog from '../dialogs/DocumentRejectDialog';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { pathwayFor } from '../../../utils/admissionAssessment';
import { ADMISSION_EMAIL } from '../../../types/email';
import { useUpcomingEmails } from '../../email/useUpcomingEmails';
import DocumentResendBar, { RejectedDocument } from './DocumentResendBar';

/**
 * Documents tab — the cycle's document checklist, one row per document:
 * upload (or replace) the file the family handed in, open it, verify or
 * reject it, and note when the originals were seen. Required documents
 * must be verified before the applicant can be enrolled. With the documents
 * email on, rejections are gathered and sent to the family in one email.
 */

interface Props {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  onChanged: () => Promise<void> | void;
}

/** Staff-facing document states — also used by the applications export. */
export const DOCUMENT_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  missing:  { label: 'Not uploaded', tone: 'neutral' },
  pending:  { label: 'Uploaded', tone: 'info' },
  uploaded: { label: 'To verify', tone: 'warning' },
  verified: { label: 'Verified', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'error' },
};

const CandidateDocumentsTab: React.FC<Props> = ({ app, cycle, onChanged }) => {
  const { canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const { enqueueSnackbar } = useSnackbar();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApplicationDocument | null>(null);

  const checklist = pathwayFor(cycle, app.classId).documentChecklist || [];
  const byKey = new Map((app.documents || []).map(d => [d.key, d]));
  const outstanding = checklist.filter(c => c.required && byKey.get(c.key)?.status !== 'verified');
  const rejected: RejectedDocument[] = checklist.flatMap(item => {
    const doc = byKey.get(item.key);
    return doc?.status === 'rejected' ? [{ key: item.key, label: item.label, reason: doc.rejectedReason ?? '', at: doc.verifiedAt ?? null }] : [];
  });

  // Whether the documents email is on, and who it would reach — only staff who can send it ask.
  const emailRequests = useMemo(
    () => (canManage ? [{ type: ADMISSION_EMAIL.documentsResend, noun: 'documents', ids: [app._id] }] : []),
    [canManage, app._id],
  );
  const upcoming = useUpcomingEmails(emailRequests);
  const emailOn = upcoming.groups.length > 0;

  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusyKey(key);
    try {
      await action();
      enqueueSnackbar(success, { variant: 'success' });
      await onChanged();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Something went wrong', { variant: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  const send = (item: DocumentChecklistItem, file: File) =>
    run(item.key, () => admissionApplicationService.uploadDocument(app._id, item.key, file), `${item.label} uploaded`);

  // Replacing a file already on record is confirmed first; a first upload isn't.
  const [replacing, setReplacing] = useState<{ item: DocumentChecklistItem; file: File; current: string } | null>(null);

  const upload = (item: DocumentChecklistItem, current?: ApplicationDocument) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (current?.fileName) setReplacing({ item, file, current: current.fileName });
    else send(item, file);
  };

  if (checklist.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 2, borderStyle: 'dashed' }}>
        <Typography variant="body2" color="text.secondary">This cycle doesn't ask for any documents.</Typography>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="body2" color={outstanding.length ? 'warning.dark' : 'success.dark'}>
          {outstanding.length
            ? `${outstanding.length} required document${outstanding.length === 1 ? '' : 's'} still to verify — needed before enrollment.`
            : 'All required documents are verified.'}
        </Typography>

        {emailOn && rejected.length > 0 && (
          <DocumentResendBar app={app} rejected={rejected} to={upcoming.groups[0].to} onSent={onChanged} />
        )}

        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          {checklist.map((item: DocumentChecklistItem, i) => {
            const doc = byKey.get(item.key);
            const status = DOCUMENT_STATUS[doc?.status ?? 'missing'];
            const busy = busyKey === item.key;
            return (
              <Box key={item.key} sx={{ px: 2, py: 1.75, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                      {item.required && <Typography variant="caption" color="text.secondary">Required</Typography>}
                      <ToneChip label={status.label} tone={status.tone} />
                      {doc?.physicallyVerifiedAt && <ToneChip label="Originals seen" tone="neutral" />}
                    </Stack>
                    {doc && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        <Link href={toUploadUrl('admissions', doc.filename)} target="_blank" rel="noopener" underline="hover">
                          {doc.fileName} <OpenInNew sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                        </Link>
                        {' · '}uploaded {dateTimeUtils.formatDateForDisplay(doc.uploadedAt)}
                      </Typography>
                    )}
                    {doc?.status === 'rejected' && doc.rejectedReason && (
                      <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>Rejected: {doc.rejectedReason}</Typography>
                    )}
                  </Box>
                  {canManage && (
                  <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                    {doc && doc.status !== 'verified' && doc.status !== 'rejected' && (
                      <Button size="small" variant="contained" disableElevation disabled={busy} sx={{ textTransform: 'none' }}
                        onClick={() => run(item.key, () => admissionApplicationService.verifyDocument(app._id, item.key), `${item.label} verified`)}>
                        Verify
                      </Button>
                    )}
                    {doc && doc.status !== 'rejected' && (
                      <Button size="small" color="inherit" disabled={busy} sx={{ textTransform: 'none', color: 'text.secondary' }} onClick={() => setRejectTarget(doc)}>
                        Reject
                      </Button>
                    )}
                    {doc && !doc.physicallyVerifiedAt && (
                      <Button size="small" color="inherit" disabled={busy} sx={{ textTransform: 'none', color: 'text.secondary' }}
                        onClick={() => run(item.key, () => admissionApplicationService.markOriginalsSeen(app._id, item.key), 'Originals noted')}>
                        Originals seen
                      </Button>
                    )}
                    <Button component="label" size="small" variant={doc ? 'text' : 'outlined'} startIcon={<UploadFile fontSize="small" />}
                      disabled={busy} sx={{ textTransform: 'none' }}>
                      {busy ? 'Working…' : doc ? 'Replace' : 'Upload'}
                      <input hidden type="file" accept="application/pdf,image/*" onChange={upload(item, doc)} aria-label={`Upload ${item.label}`} />
                    </Button>
                  </Stack>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Paper>
      </Stack>

      <ConfirmationDialog
        open={!!replacing}
        onClose={() => setReplacing(null)}
        onConfirm={() => { const r = replacing!; setReplacing(null); send(r.item, r.file); }}
        title={`Replace the ${replacing?.item.label ?? 'document'}?`}
        message="The new file takes the place of the one on record, and the document goes back to “to verify”."
        confirmText="Replace"
        confirmColor="primary"
      >
        <ConfirmFacts items={[
          { label: 'Current file', value: replacing?.current ?? '' },
          { label: 'New file', value: replacing?.file.name ?? '' },
        ]} />
      </ConfirmationDialog>

      {rejectTarget && (
        <DocumentRejectDialog
          open
          document={rejectTarget}
          applicationId={app._id}
          emailOn={emailOn}
          onCancel={() => setRejectTarget(null)}
          onRejected={async () => { setRejectTarget(null); await onChanged(); }}
        />
      )}
    </>
  );
};

export default CandidateDocumentsTab;
